/**
 * Shared admin scope types and helpers.
 * Scope lives in JWT app_metadata.admin_scope (fallback user_metadata).
 * Missing/unknown scope defaults to "super" for backward compatibility.
 */

export const ADMIN_SCOPES = [
  "super",
  "ops",
  "finance",
  "risk",
  "support",
  "fleet",
  "va",
] as const;

export type AdminScope = (typeof ADMIN_SCOPES)[number];

/** Scopes allowed to send a fresh fleet magic link on WhatsApp. */
export const FLEET_LINK_SEND_SCOPES: AdminScope[] = ["super", "ops", "fleet"];

/** Scopes allowed to register agencies via admin-create-agency */
export const AGENCY_REGISTER_SCOPES: AdminScope[] = [
  "super",
  "ops",
  "support",
  "fleet",
  "va",
];

/** Scopes allowed to view bookings (read) */
export const BOOKING_READ_SCOPES: AdminScope[] = [
  "super",
  "ops",
  "support",
];

/** Scopes allowed to mutate bookings (force status, edit, check-in/out). */
export const BOOKING_MUTATION_SCOPES: AdminScope[] = [
  "super",
  "ops",
];

/**
 * Bank refund execution (PayZone). Support and VA may request it, never run it.
 */
export const BANK_REFUND_SCOPES: AdminScope[] = ["super", "ops"];

/** Consult, approve, and reject guest dossiers. */
export const GUEST_DOSSIER_SCOPES: AdminScope[] = [
  "super",
  "ops",
  "support",
  "va",
];

/** Open a priority refund ticket for ops. Does not move money. */
export const REFUND_ESCALATION_SCOPES: AdminScope[] = [
  "super",
  "ops",
  "support",
  "va",
];

/** Unified daily queue for the VA cockpit. */
export const TODAY_QUEUE_SCOPES: AdminScope[] = [
  "super",
  "ops",
  "support",
  "va",
];

/** Scopes allowed to view abandoned checkouts (cart recovery) */
export const ABANDONED_CHECKOUT_SCOPES: AdminScope[] = [
  "super",
  "ops",
  "finance",
  "risk",
  "support",
  "va",
];

/** Nav / route allowlists used by sidebar filtering */
export const ADMIN_ROUTE_SCOPES: Record<string, AdminScope[]> = {
  "/admin": ["super", "ops", "finance", "risk", "support"],
  "/admin/users": ["super"],
  "/admin/vehicles": ["super", "ops", "support", "fleet", "va"],
  "/admin/vehicle-update-requests": ["super", "ops", "support", "fleet"],
  "/admin/agencies": ["super", "ops", "support", "fleet", "va"],
  "/admin/documents": ["super", "ops", "support"],
  "/admin/kyc": ["super", "ops", "support", "va"],
  "/admin/aujourd-hui": TODAY_QUEUE_SCOPES,
  "/admin/guest-bookings": GUEST_DOSSIER_SCOPES,
  "/admin/bookings": ["super", "ops", "support"],
  "/admin/invoices": ["super", "finance"],
  "/admin/withdraws": ["super", "finance"],
  "/admin/promo-codes": ["super", "finance"],
  "/admin/cancellations": ["super", "finance"],
  "/admin/damage-claims": ["super", "risk"],
  "/admin/chargebacks": ["super", "risk"],
  "/admin/operations": ["super", "ops", "finance", "risk"],
  "/admin/abandoned-checkouts": [
    "super",
    "ops",
    "finance",
    "risk",
    "support",
    "va",
  ],
  "/admin/platform-health": ["super", "ops", "risk"],
  "/admin/health": ["super", "ops", "risk"],
  "/admin/reminders": ["super", "ops", "support", "fleet"],
  "/admin/revenue": ["super", "finance"],
  "/admin/simulation": ["super", "finance"],
  "/admin/no-deposit-simulator": ["super", "finance"],
  "/admin/hotel-partners": ["super", "ops", "support", "finance", "va"],
};

export interface AdminNavItem {
  path: string;
  labelKey: string;
  scopes: AdminScope[];
}

/** Single admin nav list. Desktop bar, admin menu, and mobile menu all filter this. */
export const ADMIN_NAV_ITEMS: AdminNavItem[] = [
  { path: "/admin", labelKey: "nav.dashboard", scopes: ADMIN_ROUTE_SCOPES["/admin"] },
  { path: "/admin/aujourd-hui", labelKey: "nav.today", scopes: ADMIN_ROUTE_SCOPES["/admin/aujourd-hui"] },
  { path: "/admin/bookings", labelKey: "nav.bookings", scopes: ADMIN_ROUTE_SCOPES["/admin/bookings"] },
  { path: "/admin/guest-bookings", labelKey: "nav.guestBookings", scopes: ADMIN_ROUTE_SCOPES["/admin/guest-bookings"] },
  { path: "/admin/vehicles", labelKey: "nav.vehicles", scopes: ADMIN_ROUTE_SCOPES["/admin/vehicles"] },
  { path: "/admin/vehicle-update-requests", labelKey: "nav.vehicleUpdateRequests", scopes: ADMIN_ROUTE_SCOPES["/admin/vehicle-update-requests"] },
  { path: "/admin/agencies", labelKey: "nav.agencies", scopes: ADMIN_ROUTE_SCOPES["/admin/agencies"] },
  { path: "/admin/documents", labelKey: "nav.documents", scopes: ADMIN_ROUTE_SCOPES["/admin/documents"] },
  { path: "/admin/kyc", labelKey: "nav.kyc", scopes: ADMIN_ROUTE_SCOPES["/admin/kyc"] },
  { path: "/admin/users", labelKey: "nav.users", scopes: ADMIN_ROUTE_SCOPES["/admin/users"] },
  { path: "/admin/invoices", labelKey: "nav.invoices", scopes: ADMIN_ROUTE_SCOPES["/admin/invoices"] },
  { path: "/admin/revenue", labelKey: "nav.revenue", scopes: ADMIN_ROUTE_SCOPES["/admin/revenue"] },
  { path: "/admin/withdraws", labelKey: "nav.withdraws", scopes: ADMIN_ROUTE_SCOPES["/admin/withdraws"] },
  { path: "/admin/promo-codes", labelKey: "nav.promoCodes", scopes: ADMIN_ROUTE_SCOPES["/admin/promo-codes"] },
  { path: "/admin/cancellations", labelKey: "nav.cancellations", scopes: ADMIN_ROUTE_SCOPES["/admin/cancellations"] },
  { path: "/admin/simulation", labelKey: "nav.financialSimulation", scopes: ADMIN_ROUTE_SCOPES["/admin/simulation"] },
  { path: "/admin/no-deposit-simulator", labelKey: "nav.noDepositSimulator", scopes: ADMIN_ROUTE_SCOPES["/admin/no-deposit-simulator"] },
  { path: "/admin/hotel-partners", labelKey: "nav.hotelPartners", scopes: ADMIN_ROUTE_SCOPES["/admin/hotel-partners"] },
  { path: "/admin/operations", labelKey: "nav.operations", scopes: ADMIN_ROUTE_SCOPES["/admin/operations"] },
  { path: "/admin/abandoned-checkouts", labelKey: "nav.abandonedCheckouts", scopes: ADMIN_ROUTE_SCOPES["/admin/abandoned-checkouts"] },
  { path: "/admin/chargebacks", labelKey: "nav.chargebacks", scopes: ADMIN_ROUTE_SCOPES["/admin/chargebacks"] },
  { path: "/admin/damage-claims", labelKey: "nav.damageClaims", scopes: ADMIN_ROUTE_SCOPES["/admin/damage-claims"] },
  { path: "/admin/reminders", labelKey: "nav.fleetReminders", scopes: ADMIN_ROUTE_SCOPES["/admin/reminders"] },
  { path: "/admin/health", labelKey: "nav.operationalHealth", scopes: ADMIN_ROUTE_SCOPES["/admin/health"] },
  { path: "/admin/platform-health", labelKey: "nav.platformHealth", scopes: ADMIN_ROUTE_SCOPES["/admin/platform-health"] },
];

export const visibleAdminNavPaths = (scope: AdminScope): string[] =>
  ADMIN_NAV_ITEMS.filter((item) => scopeIsAllowed(scope, item.scopes)).map((item) => item.path);

/** Default landing page inside admin for a given scope */
export const getAdminHomePath = (scope: AdminScope): string => {
  if (scope === "va") return "/admin/aujourd-hui";
  if (scope === "fleet") return "/admin/vehicles";
  return "/admin";
};

export const normalizeAdminScope = (raw: string | null | undefined): AdminScope => {
  const normalized = (raw || "super").toLowerCase();
  if ((ADMIN_SCOPES as readonly string[]).includes(normalized)) {
    return normalized as AdminScope;
  }
  return "super";
};

export const readAdminScopeFromUser = (user: {
  app_metadata?: Record<string, unknown> | null;
  user_metadata?: Record<string, unknown> | null;
} | null | undefined): AdminScope => {
  const raw =
    (user?.app_metadata?.admin_scope as string | undefined) ||
    (user?.user_metadata?.admin_scope as string | undefined);
  return normalizeAdminScope(raw);
};

export const scopeIsAllowed = (
  scope: AdminScope,
  allowedScopes: readonly AdminScope[]
): boolean => allowedScopes.includes(scope);
